import React from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';

// Fetch the group's site disease colour key for the chart (TGCLOUD-1793 Phase 2).
//
// Returns `{ [diseaseId]: hex }`, and an EMPTY object whenever the feature is
// off, the request fails, or the group has pinned nothing. Empty is the safe
// answer everywhere: `applyColourKey` returns its input unchanged, so the chart
// falls back to exactly the automatic behaviour it had before the key existed.
//
// GATED ON THE FLAG BEFORE THE REQUEST, not after. With the feature off this
// issues no network call at all, so a deployment that has not opted in pays
// nothing and cannot be affected by a failure in this path.
//
// A COLOUR IS COSMETIC AND MUST NOT BE ABLE TO BREAK A CHART. Every failure
// resolves to an empty key rather than propagating, because the alternative is
// an unreadable pedigree when a settings endpoint has a bad day.
//
// STALENESS IS ACCEPTED AND DELIBERATE. The key is fetched once per chart. An
// admin pinning a colour in another tab does not repaint an already-open
// pedigree until it is reloaded. The audit called this out: colour is in no
// query key today, it lives in the AppContext reducer, and updating that reducer
// alone does not repaint fills because `memberRecords` must be rebuilt too.
// Chasing live invalidation here would mean touching that whole path; the Phase
// 3 configuration UI is the right place to decide whether it is worth it.

export function useDiseaseColourKey(enabled) {
  const [colourKey, setColourKey] = React.useState({});

  React.useEffect(() => {
    if (!enabled) {
      setColourKey({});
      return undefined;
    }

    let cancelled = false;

    axios
      .get(APICONSTANTS.diseaseColourKey)
      .then(({ data }) => {
        if (cancelled) return;
        const map = {};
        (data?.rows || []).forEach((row) => {
          // `pinned` is the authority, not presence: a row that is merely
          // recorded rather than deliberately configured must change nothing
          // on any chart (decision I).
          if (!row || row.pinned !== true) return;
          if (!row.diseaseId || typeof row.colour !== 'string') return;
          if (row.colour.trim() === '') return;
          map[String(row.diseaseId)] = row.colour;
        });
        setColourKey(map);
      })
      .catch(() => {
        if (!cancelled) setColourKey({});
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return colourKey;
}

export default useDiseaseColourKey;
